import PropTypes from 'prop-types';
import React from 'react';
import {
    isEmpty,
    checkAllUserCompaniesRolesWithCompanyIds,
} from '../../../utilities/helperFunctions';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import { pageDisplayModePropTypes } from '../../../pages/defaultProps';
import { PAGE_DISPLAY_MODE, ROLE_TYPES } from '../../../pages/constants';

import FooterDrawer from './Modules/Drawers/FooterDrawer/FooterDrawer';
import UserDrawer from './Modules/Drawers/UserDrawer/UserDrawer';
import EmployerNavbar from './Navbars/EmployerNavbar/EmployerNavbar';
import UserNavbar from './Navbars/UserNavbar/UserNavbar';
import ActionsProvider from './Providers/ActionsProvider';
import UserMenuProvider from './Providers/UserMenuProvider';

import './Header.scss';

const NavbarController = (props) => {
    switch (props.pageDisplayMode) {
        case PAGE_DISPLAY_MODE.USER_DEFAULT:
            return <UserNavbar {...props} />;

        case PAGE_DISPLAY_MODE.EMPLOYER_DEFAULT:
            return <EmployerNavbar {...props} />;

        default:
            return null;
    }
};

export default class Header extends React.PureComponent {
    constructor(props) {
        super(props);

        const { userCompaniesRoles } = props.session;
        const userCompaniesIds =
            userCompaniesRoles && Object.keys(userCompaniesRoles);
        this.userEmployerRoles = checkAllUserCompaniesRolesWithCompanyIds(
            userCompaniesIds,
            userCompaniesRoles
        );
        this.isParticipatedInQuickApply = this.checkisParticipatedInQuickApply(
            props.quickApplyCompanyIds,
            userCompaniesRoles
        );
    }

    static propTypes = {
        /**
         * Props for login widget component
         */
        loginWidgetProps: PropTypes.object.isRequired,
        /**
         * Value of the view layout the page is in
         */
        pageDisplayMode: pageDisplayModePropTypes.isRequired,
        /**
         * data for badge
         */
        badgeCountData: PropTypes.object.isRequired,
        /**
         * data for notifications
         */
        notifications: PropTypes.object.isRequired,
        /**
         * function to check whether user is logged in or not
         */
        checkIsLoggedIn: PropTypes.func.isRequired,
        /**
         * function to retreive user status
         */
        getUserStatus: PropTypes.func.isRequired,
        /**
         * whether user is an admin or not
         */
        isAdmin: PropTypes.bool.isRequired,
        /**
         * Props for footer
         */
        footerProps: PropTypes.object.isRequired,
        /**
         * session of current user
         */
        session: PropTypes.object,
        /**
         * ids of company participated in quick apply
         */
        quickApplyCompanyIds: PropTypes.array,
        /**
         * feature flags
         */
        featureFlags: PropTypes.object,
    };

    static defaultProps = {
        pageDisplayMode: PAGE_DISPLAY_MODE.USER_DEFAULT,
        loginWidgetProps: {},
        getUserStatus: () => {},
        quickApplyCompanyIds: [],
        featureFlags: {},
    };

    checkisParticipatedInQuickApply = (
        participatedCompanyIds = [],
        userCompaniesRoles = {}
    ) => {
        const userEmployerRoles = checkAllUserCompaniesRolesWithCompanyIds(
            participatedCompanyIds,
            userCompaniesRoles
        );

        const noEmployerRole = Object.values(userEmployerRoles).every(
            (employerRole) => employerRole === false
        );

        return !noEmployerRole;
    };

    getUserRole = (userEmployerRoles = {}) => {
        const { checkIsLoggedIn, session, isAdmin } = this.props;
        const isAuthenticated = checkIsLoggedIn();

        if (!isAuthenticated || isEmpty(session)) {
            return ROLE_TYPES.UNAUTH;
        }

        if (
            isAdmin ||
            ['eventsManager', 'adManager', 'customerSuccess'].some(
                (role) =>
                    session.additionalRoles &&
                    session.additionalRoles.includes(role)
            )
        ) {
            return ROLE_TYPES.USER_ADMIN;
        }

        if (userEmployerRoles.isAdmin) {
            return ROLE_TYPES.EMPLOYER_ADMIN;
        }

        if (userEmployerRoles.isRecruiter) {
            return ROLE_TYPES.EMPLOYER_RECRUITER;
        }

        if (userEmployerRoles.isEmployee) {
            return ROLE_TYPES.EMPLOYER_DEFAULT;
        }

        if (
            session.additionalRoles &&
            session.additionalRoles.indexOf('author') >= 0
        ) {
            return ROLE_TYPES.USER_CONTRIBUTOR;
        }

        return ROLE_TYPES.USER_DEFAULT;
    };

    checkIfAuthorizedForEmployerNav = (userRole) => {
        return (
            userRole === ROLE_TYPES.USER_ADMIN ||
            userRole === ROLE_TYPES.EMPLOYER_DEFAULT ||
            userRole === ROLE_TYPES.EMPLOYER_RECRUITER ||
            userRole === ROLE_TYPES.EMPLOYER_ADMIN
        );
    };

    handleMetricEvent = (metricEventType = null) => () => {
        if (metricEventType) {
            EmitMetric({
                misc_event_count: 1,
                misc_event_type: metricEventType,
            });
        }
    };

    render() {
        const {
            session,
            loginWidgetProps,
            badgeCountData,
            notifications,
            footerProps,
            checkIsLoggedIn,
            pageDisplayMode,
            featureFlags,
        } = this.props;

        const userRole = this.getUserRole(this.userEmployerRoles);
        const athorizedForEmployerNav = this.checkIfAuthorizedForEmployerNav(
            userRole
        );
        const profileImage =
            (session && session.logo) || '/v1/avatar_black_160_vtg7lw.svg';
        const sessionUsername = session && session.username;

        return (
            <header className="component-Layout-Header">
                <ActionsProvider>
                    {({ setActionState, getActionState }) => (
                        <UserMenuProvider
                            userRole={userRole}
                            pageDisplayMode={pageDisplayMode}
                            badgeCountData={badgeCountData}
                            isParticipatedInQuickApply={
                                this.isParticipatedInQuickApply
                            }
                            featureFlags={featureFlags}
                        >
                            {({
                                userDrawerMenu,
                                userDropDownMenu,
                                hasEmployerMessageInUserMenu,
                            }) => (
                                <React.Fragment>
                                    <NavbarController
                                        featureFlags={featureFlags}
                                        pageDisplayMode={pageDisplayMode}
                                        userRole={userRole}
                                        session={session}
                                        setActionState={setActionState}
                                        getActionState={getActionState}
                                        badgeCountData={badgeCountData}
                                        notifications={notifications}
                                        athorizedForEmployerNav={
                                            athorizedForEmployerNav
                                        }
                                        isLoggedIn={checkIsLoggedIn()}
                                        handleMetricEvent={
                                            this.handleMetricEvent
                                        }
                                        hasEmployerMessageInUserMenu={
                                            hasEmployerMessageInUserMenu
                                        }
                                        loginWidgetProps={loginWidgetProps}
                                        userDropdownProps={{
                                            isDropdownOpen: getActionState(
                                                'isUserDropdownOpen'
                                            ),
                                            profileImage,
                                            headerText: sessionUsername,
                                            onClose: () =>
                                                setActionState(
                                                    'isUserDropdownOpen',
                                                    false
                                                ),
                                            menuItems: userDropDownMenu || [],
                                        }}
                                    />
                                    <FooterDrawer
                                        pageDisplayMode={pageDisplayMode}
                                        featureFlags={featureFlags}
                                        shouldShowLogo={true}
                                        footerProps={footerProps}
                                        handleClose={() =>
                                            setActionState(
                                                'isLeftDrawerOpen',
                                                false
                                            )
                                        }
                                        isDrawerOpen={getActionState(
                                            'isLeftDrawerOpen'
                                        )}
                                    />

                                    <UserDrawer
                                        featureFlags={featureFlags}
                                        pageDisplayMode={pageDisplayMode}
                                        profileImage={session && session.logo}
                                        headerText={session && session.username}
                                        handleClose={() =>
                                            setActionState(
                                                'isUserDrawerOpen',
                                                false
                                            )
                                        }
                                        isDrawerOpen={getActionState(
                                            'isUserDrawerOpen'
                                        )}
                                        direction="right"
                                        menuItems={userDrawerMenu || []}
                                    />
                                </React.Fragment>
                            )}
                        </UserMenuProvider>
                    )}
                </ActionsProvider>
            </header>
        );
    }
}
